import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type ManualAttachment } from './api.ts'
import type { PrescriptionPage } from './usePrescriptionPages.ts'

/**
 * The receitas people attached to one conferência, and the two things a
 * person can do with them (#276).
 *
 * Shared by both review modes, like `usePrescriptionPages`, so the button in
 * the header and the list above the viewer read the same state.
 *
 * `revision` counts the changes made from here. The pages hook takes it to
 * know that what it is showing no longer matches what will be read.
 */
export function useManualAttachments(
  verificationId: string,
  /** Called once a change is stored and the analysis has been asked to run again. */
  onChanged: () => void,
) {
  const [attachments, setAttachments] = useState<readonly ManualAttachment[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [revision, setRevision] = useState(0)
  /** Whether the list has been read once: before that, nothing is known to be removed. */
  const [loaded, setLoaded] = useState(false)
  /**
   * The files attached from this screen, shown from the browser's own copy
   * until the run that reads them lists their pages. Keyed by attachment id.
   */
  const [previews, setPreviews] = useState<readonly Preview[]>([])
  /** The attachment last added from here, for the viewer to bring on screen. */
  const [latest, setLatest] = useState<string | undefined>()
  const made = useRef<string[]>([])
  useEffect(
    () => () => {
      // A blob URL holds the file in memory until it is revoked.
      for (const url of made.current) URL.revokeObjectURL(url)
    },
    [],
  )

  const load = useCallback(async () => {
    try {
      setAttachments((await api.manualAttachments(verificationId)).attachments)
      setLoaded(true)
    } catch {
      // The list is an addition to the screen; the conferência works without it.
    }
  }, [verificationId])

  useEffect(() => {
    void load()
  }, [load])

  async function change<T>(work: () => Promise<T>, failure: string, then?: (result: T) => void) {
    setBusy(true)
    setError(undefined)
    try {
      const result = await work()
      then?.(result)
      setRevision((n) => n + 1)
      await load()
      onChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : failure)
    } finally {
      setBusy(false)
    }
  }

  return {
    attachments,
    loaded,
    busy,
    error,
    revision,
    previews,
    latest,
    attach: (file: File) =>
      change(
        () => api.attachReceita(verificationId, file),
        'falha ao anexar a receita',
        (attached) => {
          setLatest(attached.id)
          if (!DRAWABLE.has(file.type)) return
          const url = URL.createObjectURL(file)
          made.current.push(url)
          setPreviews((current) => [...current, { attachmentId: attached.id, url }])
        },
      ),
    remove: (attachmentId: string) =>
      change(() => api.removeReceita(verificationId, attachmentId), 'falha ao remover a receita'),
  }
}

export type ManualAttachments = ReturnType<typeof useManualAttachments>

/** A file attached from this screen, as the browser can draw it right away. */
export interface Preview {
  readonly attachmentId: string
  readonly url: string
}

/**
 * What an <img> draws as the file is. A PDF or a TIFF has to be rendered by
 * the server first, so those wait for the run like before.
 */
const DRAWABLE = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif'])

/**
 * The pages to show while the documents are changing under the screen (#276).
 *
 * The page list is fetched again only when the run that follows an attach or
 * a removal ends. Until then the screen showed the old set: a removed receita
 * stayed through a run that no longer read it, and an attached one did not
 * appear until the run was over. So a manual page whose attachment is gone is
 * dropped straight away, and a file just attached is shown from the browser's
 * own copy until its real pages are listed. The ERP's pages are never touched,
 * and nothing is dropped before the list of attachments has been read.
 */
export function shownPages(
  pages: readonly PrescriptionPage[],
  manual: Pick<ManualAttachments, 'attachments' | 'loaded' | 'previews'>,
): readonly PrescriptionPage[] {
  if (!manual.loaded) return pages
  const kept = new Set(manual.attachments.map((one) => one.id))
  const listed = pages.filter((page) => page.source !== 'manual' || kept.has(page.attachmentId))
  const known = new Set(listed.map((page) => page.attachmentId))
  const waiting = manual.previews
    .filter((one) => kept.has(one.attachmentId) && !known.has(one.attachmentId))
    .map((one, index): PrescriptionPage => ({
      url: one.url,
      // Below every page a block can cite, so no highlight lands on it.
      page: -1 - index,
      source: 'manual',
      attachmentId: one.attachmentId,
    }))
  return [...listed, ...waiting]
}
